import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { BiddingContext } from '@/context/BiddingContext';
import { priceWithMinDeliveryExpenses } from '@/util/price';
import { useHermesEntry } from '@/hooks/useHermesEntry';
import {
  CalculatorType,
  clearOpLaskuriOptions,
  setOpLaskuriOptions,
  getWidgetUrl,
} from '@/features/EntryPage/FinancingOptions/OPRahoitus/util/op-laskuri';

export const usePriceWithMinDeliveryExpenses = () => {
  const [totalPrice, setTotalPrice] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(0);

  const { entryId, vatPerc, startPrice, minimumRaise, deliveryMethods, processingCostInfo } =
    useContext(BiddingContext);
  const { hermesEntry } = useHermesEntry(entryId, vatPerc);

  useLayoutEffect(() => {
    if (!hermesEntry) return;
    const totalSum = priceWithMinDeliveryExpenses(
      hermesEntry.highestBid,
      startPrice,
      minimumRaise,
      vatPerc,
      deliveryMethods,
      processingCostInfo
    );
    setTotalPrice(totalSum);
    setCurrentPrice(hermesEntry.highestBid ?? 0);
  }, [hermesEntry, startPrice, minimumRaise, vatPerc, deliveryMethods, processingCostInfo]);

  return { totalPrice, currentPrice };
};

export const useOpRahoitus = (calculatorType: CalculatorType, maxPrice: number) => {
  const MinimumLoanAmount = 2000;
  const { totalPrice, currentPrice } = usePriceWithMinDeliveryExpenses();

  const loanAmount = Math.min(Math.round(totalPrice / 1000) * 1000 + MinimumLoanAmount, maxPrice);
  const loanAmountMin = Math.max(Math.round(totalPrice), MinimumLoanAmount);

  const scriptPending = useRef(false);

  useLayoutEffect(() => {
    const id = 'op-widget-script';

    if (!scriptPending.current && !document.getElementById(id)) {
      const script = document.createElement('script');
      script.id = id;
      script.src = `${getWidgetUrl()}/op-calc-widget.js`;
      script.async = true;
      script.onload = () => {
        scriptPending.current = false;
      };
      document.head.appendChild(script);
      scriptPending.current = true;
    }

    return () => document.getElementById(id)?.remove();
  }, []);

  useLayoutEffect(() => {
    const options = { calculatorType, loanAmount, loanAmountMin };
    setOpLaskuriOptions(options);
    return () => {
      clearOpLaskuriOptions();
    };
  }, [calculatorType, loanAmount, loanAmountMin]);

  return { currentPrice, loan: { loanAmount, loanAmountMin } };
};
